import { CSSProperties, useRef } from "react";

import { useRect } from "@reach/rect";

import { ADF } from "@atlassiansox/bxp-content-framework";
import { RichText } from "@atlassiansox/bxp-content-framework";

import { useCoordinates, useViewportDimensions } from "../../components";
import { colorCSS, zIndex } from "../../styles";

export type TooltipContentProps = {
    toolTipText: ADF.Document;
    trigger: React.MutableRefObject<HTMLElement | null>;
};

export const TooltipContent = ({
    toolTipText,
    trigger
}: TooltipContentProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const rect = useRect(ref);
    const triggerRect = useRect(trigger);
    const { viewportWidth } = useViewportDimensions();
    const coordinates = useCoordinates(trigger);

    const height = rect ? rect.bottom - rect.top : 0;
    const isTop = triggerRect ? triggerRect.top > height : true;

    return (
        <div>
            <div
                style={getTooltipArrowPosition({
                    coordinates,
                    isTop,
                    hide: height === 0
                })}
            />
            <div
                ref={ref}
                className="tooltip-content"
                data-testid="tooltip-content"
                style={getTooltipContentPosition({
                    coordinates,
                    viewportWidth,
                    isTop,
                    height
                })}
            >
                <RichText document={toolTipText} />
            </div>
        </div>
    );
};

export type GetTooltipContentProps = {
    readonly coordinates: [number, number];
    readonly viewportWidth: number;
    readonly isTop: boolean;
    readonly height: number;
};

export function getTooltipContentPosition({
    coordinates,
    viewportWidth,
    isTop,
    height
}: GetTooltipContentProps) {
    const [x, y] = coordinates;
    const width = 200;
    const half = width / 2;
    const edgeOffset = 8;
    const translateX = `${-x + edgeOffset}px`;
    const translateY = isTop ? `calc(-1rem - ${height}px)` : "1.5rem";
    const horizontalPosition =
        viewportWidth - x < half ? "right" : x < half ? "left" : "center";

    const css: CSSProperties = {
        backgroundColor: `${colorCSS("B500")}`,
        borderRadius: "5px",
        boxShadow: isTop
            ? "2px -2px 4px rgba(0, 0, 0, 0.25)"
            : "2px 2px 4px rgba(0, 0, 0, 0.25)",
        color: `${colorCSS("N0")}`,
        fontSize: "0.875rem",
        left: horizontalPosition === "right" ? "auto" : `${x}px`,
        opacity: height === 0 ? "0" : "1",
        padding: "0.75rem",
        paddingTop: "0.5rem",
        position: "absolute",
        right: horizontalPosition === "right" ? `${edgeOffset}px` : "auto",
        top: `${y}px`,
        transform:
            horizontalPosition === "right"
                ? `translate3d(0, ${translateY}, 0)`
                : horizontalPosition === "left"
                ? `translate3d(${translateX}, ${translateY}, 0)`
                : `translate3d(-50%, ${translateY}, 0)`,
        width: `${width}px`,
        zIndex: isTop ? zIndex.tooltip : 1
    };

    return css;
}

export type GetTootipArrowProps = {
    readonly coordinates: [number, number];
    readonly isTop: boolean;
    readonly hide: boolean;
};

export function getTooltipArrowPosition({
    coordinates,
    isTop,
    hide
}: GetTootipArrowProps) {
    const [x, y] = coordinates;

    const css: CSSProperties = {
        borderBottom: isTop ? "none" : `0.5rem solid ${colorCSS("B500")}`,
        borderLeft: `0.5rem solid transparent`,
        borderRight: `0.5rem solid transparent`,
        borderTop: isTop ? `0.5rem solid ${colorCSS("B500")}` : "none",
        display: "block",
        height: 0,
        left: `${x}px`,
        opacity: hide ? "0" : "1",
        position: "absolute",
        top: isTop ? `${y - 2}px` : `${y + 2}px`,
        transform: isTop
            ? "translate3d(-0.5rem, -1rem, 0)"
            : "translate3d(-0.5rem, 1rem, 0)",
        width: 0
    };

    return css;
}
